import React, { useState } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';

// ===== STYLED COMPONENTS =====
const PrivacyContainer = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  background: #DCD5C9;
  color: #000;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const PrivacyWrapper = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 0;
  width: 100%;
  max-width: 100%;
  
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarWrapper = styled.div`
  display: block;
  
  @media screen and (max-width: 1024px) {
    display: none;
  }
`;

const ContentArea = styled.main`
  padding: 40px;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  
  @media screen and (max-width: 768px) {
    padding: 24px;
  }

  @media screen and (max-width: 480px) {
    padding: 16px;
  }
`;

const Section = styled.section`
  margin-bottom: 40px;
  
  @media screen and (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media screen and (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 5vw, 32px);
  font-weight: 600;
  margin-bottom: 20px;
  color: #690A62;
  font-family: 'PlayfairDisplaySC-Regular', serif;
  
  @media screen and (max-width: 480px) {
    font-size: clamp(20px, 4vw, 24px);
    margin-bottom: 15px;
  }
`;

const Paragraph = styled.p`
  font-size: clamp(14px, 2vw, 16px);
  line-height: 1.8;
  margin-bottom: 16px;
  color: #333;
  
  @media screen and (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 12px;
    line-height: 1.6;
  }
`;

const List = styled.ul`
  margin-left: 24px;
  margin-bottom: 20px;
  font-size: clamp(14px, 2vw, 16px);
  line-height: 1.8;
  
  li {
    margin-bottom: 12px;
    color: #333;
  }

  @media screen and (max-width: 480px) {
    margin-left: 20px;
    font-size: 14px;
    
    li {
      margin-bottom: 10px;
    }
  }
`;

const ContactBox = styled.div`
  background: rgba(105, 10, 98, 0.08);
  border-left: 4px solid #690A62;
  padding: 20px;
  margin: 30px 0;
  border-radius: 4px;
  box-sizing: border-box;
  
  a {
    color: #690A62;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (max-width: 480px) {
    padding: 16px;
    margin: 20px 0;
  }
`;

// ===== RESPONSIVE TABLE COMPONENT =====
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 30px 0;
  border-radius: 8px;
  box-sizing: border-box;
  
  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-x: visible;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  th {
    background: #690A62;
    color: #fff;
    padding: 16px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #e0e0e0;
    font-size: clamp(13px, 2vw, 15px);
    line-height: 1.6;
  }

  tr:hover {
    background: #f9f9f9;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

// ===== MOBILE CARD TABLE =====
const CardTable = styled.div`
  display: none;
  
  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  box-sizing: border-box;
  
  @media screen and (max-width: 480px) {
    border-radius: 6px;
  }
`;

const CardRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }

  @media screen and (max-width: 480px) {
    flex-direction: column;
  }
`;

const CardLabel = styled.div`
  flex: 0 0 40%;
  background: #f5f5f5;
  padding: 12px 16px;
  font-weight: 600;
  color: #690A62;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  
  @media screen and (max-width: 480px) {
    flex: 1;
    background: #690A62;
    color: #fff;
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const CardValue = styled.div`
  flex: 1;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  
  @media screen and (max-width: 480px) {
    padding: 12px;
    background: #fafafa;
    font-size: 13px;
  }
`;

// ===== COMPONENT =====
const Privacy = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <Navbar toggle={() => setMobileMenuOpen(!mobileMenuOpen)} />
      <PrivacyContainer>
        <PrivacyWrapper>
          <SidebarWrapper>
            <Sidebar isOpen={mobileMenuOpen} toggle={() => setMobileMenuOpen(false)} />
          </SidebarWrapper>

          <ContentArea>
            {/* INTRO SECTION */}
            <Section>
              <SectionTitle>Polityka Prywatności</SectionTitle>
              <Paragraph>
                Dbamy o Twoją prywatność i chcemy, abyś w czasie korzystania z naszych usług czuł się komfortowo. Dlatego też poniżej prezentujemy Ci najważniejsze informacje o zasadach przetwarzania przez nas Twoich danych osobowych oraz plikach cookies, które są wykorzystywane przez naszą Stronę. Informacje te zostały przygotowane z uwzględnieniem RODO, czyli ogólnego rozporządzenia o ochronie danych.
              </Paragraph>
            </Section>

            {/* ADMINISTRATOR INFO */}
            <Section>
              <SectionTitle>Administrator Danych</SectionTitle>
              <ContactBox>
                <Paragraph>
                  <strong>ANETA WOJCIECHOWSKA</strong> – przedsiębiorca prowadzący działalność gospodarczą
                </Paragraph>
                <Paragraph>
                  <strong>NIP:</strong> 8952218294<br/>
                  <strong>REGON:</strong> 386220685<br/>
                  <strong>Adres:</strong> ul. Na Polance 6A/15, 51-109 Wrocław
                </Paragraph>
                <Paragraph>
                  <strong>E-mail:</strong> <a href="mailto:coaching@atenida.pl">coaching@atenida.pl</a>
                </Paragraph>
              </ContactBox>
            </Section>

            {/* YOUR RIGHTS */}
            <Section>
              <SectionTitle>Twoje Prawa</SectionTitle>
              <Paragraph>
                Przysługuje Ci prawo żądania:
              </Paragraph>
              <List>
                <li>dostępu do Twoich danych osobowych (art. 15 RODO)</li>
                <li>ich sprostowania (art. 16 RODO)</li>
                <li>usunięcia (art. 17 RODO)</li>
                <li>ograniczenia przetwarzania (art. 18 RODO)</li>
                <li>przeniesienia danych do innego administratora (art. 20 RODO)</li>
              </List>

              <Paragraph>
                A także prawo wniesienia sprzeciwu wobec przetwarzania Twoich danych z przyczyn związanych z Twoją szczególną sytuacją.
              </Paragraph>

              <ContactBox>
                <Paragraph>
                  ✉️ Skontaktuj się z nami, jeśli chcesz skorzystać ze swoich praw. Jeśli uznasz, że Twoje dane są przetwarzane niezgodnie z prawem, możesz złożyć skargę do <strong>Prezesa Urzędu Ochrony Danych Osobowych</strong>.
                </Paragraph>
              </ContactBox>
            </Section>

            {/* DATA PROCESSING TABLE */}
            <Section>
              <SectionTitle>Przetwarzanie Twoich Danych</SectionTitle>
              
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Cel</th>
                      <th>Podstawa Prawna</th>
                      <th>Czas Przetwarzania</th>
                      <th>Brak Danych</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Realizacja zamówienia</td>
                      <td>Umowa sprzedaży (art. 6 ust. 1 lit. b RODO)</td>
                      <td>Przez okres obowiązywania umowy + okres przedawnienia roszczeń</td>
                      <td>Brak możliwości złożenia zamówienia</td>
                    </tr>
                    <tr>
                      <td>Prowadzenie konta</td>
                      <td>Umowa o świadczenie usług (art. 6 ust. 1 lit. b RODO)</td>
                      <td>Do usunięcia konta + okres przedawnienia</td>
                      <td>Brak możliwości założenia konta</td>
                    </tr>
                    <tr>
                      <td>Obsługa zapytań</td>
                      <td>Umowa lub prawnie uzasadniony interes (art. 6 ust. 1 lit. b/f RODO)</td>
                      <td>Czas trwania umowy + okres przedawnienia</td>
                      <td>Brak możliwości odpowiedzi</td>
                    </tr>
                    <tr>
                      <td>Marketing bezpośredni</td>
                      <td>Prawnie uzasadniony interes (art. 6 ust. 1 lit. f RODO)</td>
                      <td>Do usunięcia cookies lub wyrażenia sprzeciwu</td>
                      <td>Brak sugestii produktów</td>
                    </tr>
                    <tr>
                      <td>Materiały marketingowe</td>
                      <td>Twoja zgoda (art. 6 ust. 1 lit. a RODO)</td>
                      <td>Do wycofania zgody</td>
                      <td>Brak materiałów promocyjnych</td>
                    </tr>
                  </tbody>
                </Table>

                <CardTable>
                  {[
                    { cel: 'Realizacja zamówienia', podstawa: 'Umowa sprzedaży (art. 6 ust. 1 lit. b RODO)', czas: 'Przez okres obowiązywania umowy + okres przedawnienia', brak: 'Brak możliwości złożenia zamówienia' },
                    { cel: 'Prowadzenie konta', podstawa: 'Umowa o świadczenie usług (art. 6 ust. 1 lit. b RODO)', czas: 'Do usunięcia konta + okres przedawnienia', brak: 'Brak możliwości założenia konta' },
                    { cel: 'Obsługa zapytań', podstawa: 'Umowa lub prawnie uzasadniony interes (art. 6 ust. 1 lit. b/f RODO)', czas: 'Czas trwania umowy + okres przedawnienia', brak: 'Brak możliwości odpowiedzi' },
                    { cel: 'Marketing bezpośredni', podstawa: 'Prawnie uzasadniony interes (art. 6 ust. 1 lit. f RODO)', czas: 'Do usunięcia cookies lub wyrażenia sprzeciwu', brak: 'Brak sugestii produktów' },
                    { cel: 'Materiały marketingowe', podstawa: 'Twoja zgoda (art. 6 ust. 1 lit. a RODO)', czas: 'Do wycofania zgody', brak: 'Brak materiałów promocyjnych' },
                  ].map((row, idx) => (
                    <Card key={idx}>
                      <CardRow>
                        <CardLabel>Cel</CardLabel>
                        <CardValue>{row.cel}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Podstawa</CardLabel>
                        <CardValue>{row.podstawa}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Czas</CardLabel>
                        <CardValue>{row.czas}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Brak Danych</CardLabel>
                        <CardValue>{row.brak}</CardValue>
                      </CardRow>
                    </Card>
                  ))}
                </CardTable>
              </TableContainer>
            </Section>

            {/* COOKIES SECTION */}
            <Section>
              <SectionTitle>Pliki Cookies</SectionTitle>
              <Paragraph>
                Nasz sklep, jak większość witryn internetowych, korzysta z tzw. plików cookies (ciasteczek). Pliki te są zapisywane w pamięci Twojego urządzenia i nie powodują zmian w ustawieniach.
              </Paragraph>

              <Paragraph>
                <strong>Ciasteczka wykorzystywane w celach:</strong>
              </Paragraph>
              <List>
                <li>Statystycznych – śledzenie ruchu dla analizy</li>
                <li>Marketingowych – spersonalizowanie reklam</li>
              </List>

              <ContactBox>
                <Paragraph>
                  🔧 <strong>Zarządzanie cookies:</strong> W każdej chwili możesz usunąć ciasteczka lub blokować ich obsługę w ustawieniach przeglądarki.
                </Paragraph>
                <List>
                  <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Firefox</a></li>
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a></li>
                  <li><a href="https://support.apple.com/en-us/HT201265" target="_blank" rel="noopener noreferrer">Safari</a></li>
                </List>
              </ContactBox>
            </Section>

            {/* DATA RECIPIENTS */}
            <Section>
              <SectionTitle>Odbiorcy Danych</SectionTitle>
              <Paragraph>
                Korzystamy z usług podmiotów zewnętrznych. Poniżej lista odbiorców Twoich danych:
              </Paragraph>

              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Działanie</th>
                      <th>Odbiorcy Danych</th>
                      <th>Poza UE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Każde działanie w Sklepie</td>
                      <td>Dostawca wsparcia technicznego/IT, dostawca oprogramowania e-commerce</td>
                      <td>Nie</td>
                    </tr>
                    <tr>
                      <td>Marketing</td>
                      <td>Podmiot zapewniający usługi marketingowe</td>
                      <td>Nie</td>
                    </tr>
                    <tr>
                      <td>Analityka</td>
                      <td>Podmiot umożliwiający działania analityczne</td>
                      <td>Nie</td>
                    </tr>
                    <tr>
                      <td>Płatności</td>
                      <td>Dostawca płatności, dostawca ratalu</td>
                      <td>Nie</td>
                    </tr>
                    <tr>
                      <td>Dostawa</td>
                      <td>Dostawca do Ciebie (opcja wysyłki)</td>
                      <td>Nie</td>
                    </tr>
                    <tr>
                      <td>Czat online</td>
                      <td>Dostawca czatu</td>
                      <td>Nie</td>
                    </tr>
                  </tbody>
                </Table>

                <CardTable>
                  {[
                    { działanie: 'Każde działanie w Sklepie', odbiorcy: 'Dostawca wsparcia technicznego/IT, dostawca oprogramowania e-commerce', poza: 'Nie' },
                    { działanie: 'Marketing', odbiorcy: 'Podmiot zapewniający usługi marketingowe', poza: 'Nie' },
                    { działanie: 'Analityka', odbiorcy: 'Podmiot umożliwiający działania analityczne', poza: 'Nie' },
                    { działanie: 'Płatności', odbiorcy: 'Dostawca płatności, dostawca ratalu', poza: 'Nie' },
                    { działanie: 'Dostawa', odbiorcy: 'Dostawca do Ciebie', poza: 'Nie' },
                    { działanie: 'Czat online', odbiorcy: 'Dostawca czatu', poza: 'Nie' },
                  ].map((row, idx) => (
                    <Card key={idx}>
                      <CardRow>
                        <CardLabel>Działanie</CardLabel>
                        <CardValue>{row.działanie}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Odbiorcy</CardLabel>
                        <CardValue>{row.odbiorcy}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Poza UE</CardLabel>
                        <CardValue>{row.poza}</CardValue>
                      </CardRow>
                    </Card>
                  ))}
                </CardTable>
              </TableContainer>
            </Section>

            {/* SECURITY */}
            <Section>
              <SectionTitle>Bezpieczeństwo Danych</SectionTitle>
              <Paragraph>
                Przetwarzając Twoje dane osobowe stosujemy środki organizacyjne i techniczne zgodne z właściwymi przepisami prawa, w tym <strong>szyfrowanie połączenia za pomocą certyfikatu SSL</strong>.
              </Paragraph>
            </Section>

            {/* FOOTER CONTACT */}
            <ContactBox>
              <Paragraph>
                ❓ Masz pytania? Napisz do nas na <a href="mailto:coaching@atenida.pl">coaching@atenida.pl</a>
              </Paragraph>
            </ContactBox>
          </ContentArea>
        </PrivacyWrapper>
      </PrivacyContainer>
      <Footer />
    </>
  );
};

export default Privacy;